/* eslint-disable no-use-before-define, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../../lib/api';
import { DRESS_CODE_MOODBOARD_FALLBACK } from '../../lib/defaultThemeAssets';
import './madras.css';

export default function MadrasEclatant({ wedding, heroImage, accentImage, isSectionVisible }) {
    const [navbarScrolled, setNavbarScrolled] = useState(false);

    // Guestbook state
    const [newMessage, setNewMessage] = useState({ guest_name: "", content: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messages, setMessages] = useState([]);

    // Dress Code state
    const [copiedColor, setCopiedColor] = useState(null);
    const handleCopy = (color) => {
        navigator.clipboard.writeText(color);
        setCopiedColor(color);
        setTimeout(() => setCopiedColor(null), 1500);
    };

    const fetchMessages = useCallback(async () => {
        try {
            const r = await api.get(`/public/wedding/${wedding.slug}/messages`);
            setMessages(r.data);
        } catch (e) {
            console.error("Erreur messages:", e);
        }
    }, [wedding?.slug]);

    const showSection = useCallback((id) => typeof isSectionVisible === 'function' ? isSectionVisible(id) : true, [isSectionVisible]);

    useEffect(() => {
        if (showSection("guestbook") && wedding?.slug) {
            fetchMessages();
        }
    }, [wedding?.slug, showSection, fetchMessages]);

    const handleMessageSubmit = async (e) => {
        e.preventDefault();
        if (!newMessage.guest_name || !newMessage.content) return;
        setIsSubmitting(true);
        try {
            await api.post(`/public/wedding/${wedding.slug}/messages`, newMessage);
            setNewMessage({ guest_name: "", content: "" });
            toast.success("Message envoyé !");
            fetchMessages();
        } catch (err) {
            toast.error("Erreur lors de l'envoi");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Gifts state
    const [gifts, setGifts] = useState([]);
    const [claimingGift, setClaimingGift] = useState(null);
    const [guestName, setGuestName] = useState("");
    const [contributionAmount, setContributionAmount] = useState("");
    const [isClaiming, setIsClaiming] = useState(false);

    useEffect(() => {
        if (wedding?.slug && wedding?.show_gifts !== false) {
            api.get(`/public/wedding/${wedding.slug}/gifts`).then((r) => setGifts(r.data)).catch(() => {});
        }
    }, [wedding?.slug, wedding?.show_gifts]);

    const handleClaimGift = async (e) => {
        e.preventDefault();
        if (!guestName || !claimingGift) return;
        setIsClaiming(true);
        try {
            if (claimingGift.type === "cash") {
                const amount = Number(contributionAmount);
                if (!Number.isFinite(amount) || amount <= 0) {
                    toast.error("Veuillez indiquer un montant valide.");
                    return;
                }
                const response = await api.post("/stripe/checkout", {
                    gift_id: claimingGift.id,
                    amount,
                    guest_name: guestName,
                    rsvp_token: "",
                });
                if (response.data?.url) {
                    window.location.assign(response.data.url);
                    return;
                }
                throw new Error("Lien de contribution indisponible");
            }
            await api.post(`/public/wedding/${wedding.slug}/gifts/${claimingGift.id}/claim`, { guest_name: guestName });
            const r = await api.get(`/public/wedding/${wedding.slug}/gifts`);
            setGifts(r.data);
            setClaimingGift(null);
            setGuestName("");
            toast.success("Cadeau réservé avec succès !");
        } catch (e) {
            toast.error("Erreur lors de la réservation");
        } finally {
            setIsClaiming(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setNavbarScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const dateStr = wedding?.date ? format(parseISO(wedding.date), 'dd MMMM yyyy', { locale: fr }) : "Date à venir";

    return (
        <div className="text-on-surface font-body-md overflow-x-hidden bg-surface-bright madras-theme" style={{
            '--on-error-container': '#93000a',
            '--surface-container-high': '#e9e8e2',
            '--surface-tint': '#795900',
            '--outline': '#827660',
            '--on-surface-variant': '#4f4632',
            '--on-tertiary-fixed-variant': '#005235',
            '--on-error': '#ffffff',
            '--on-tertiary-fixed': '#002113',
            '--primary': '#795900',
            '--primary-fixed-dim': '#fabd0d',
            '--surface-bright': '#fafaf3',
            '--surface': '#fafaf3',
            '--on-secondary': '#ffffff',
            '--surface-dim': '#dbdad4',
            '--background': '#fafaf3',
            '--error-container': '#ffdad6',
            '--tertiary-fixed-dim': '#6edba7',
            '--on-secondary-fixed-variant': '#920023',
            '--inverse-on-surface': '#f2f1ea',
            '--secondary-fixed-dim': '#ffb3b4',
            '--on-primary-fixed': '#261a00',
            '--on-primary-container': '#664b00',
            '--secondary-fixed': '#ffdad9',
            '--on-primary-fixed-variant': '#5b4300',
            '--on-primary': '#ffffff',
            '--inverse-primary': '#fabd0d',
            '--error': '#ba1a1a',
            '--tertiary-fixed': '#8bf8c2',
            '--on-surface': '#1b1c18',
            '--secondary-container': '#da3148',
            '--primary-container': '#f5b800',
            '--on-background': '#1b1c18',
            '--surface-variant': '#e3e3dc',
            '--surface-container-highest': '#e3e3dc',
            '--primary-fixed': '#ffdf9f',
            '--on-tertiary-container': '#005b3c',
            '--on-secondary-container': '#fffbff',
            '--secondary': '#b71032',
            '--on-secondary-fixed': '#40000a',
            '--inverse-surface': '#30312c',
            '--tertiary-container': '#69d6a3',
            '--tertiary': '#006c48',
            '--surface-container-lowest': '#ffffff',
            '--surface-container': '#efeee7',
            '--on-tertiary': '#ffffff',
            '--surface-container-low': '#f5f4ed',
            '--outline-variant': '#d4c5ac',
            '--font-headline-lg-mobile': '"Playfair Display"',
            '--font-display-lg': '"Playfair Display"',
            '--font-headline-lg': '"Playfair Display"',
            '--font-body-md': '"Libre Franklin"',
            '--font-label-caps': '"Libre Franklin"',
            '--font-display-md': '"Playfair Display"',
            '--font-body-lg': '"Libre Franklin"'
        }}>
            {/* TopNavBar */}
            <header className={`w-full top-0 sticky border-b border-outline-variant z-50 transition-colors duration-300 ${navbarScrolled ? 'bg-surface-bright/90 backdrop-blur-md shadow-sm' : 'bg-surface-bright'}`}>
                <div className="flex justify-between items-center px-margin-mobile md:px-margin-desktop py-4 max-w-container-max mx-auto">
                    <div className="h-10">
                        <span className="font-display-md text-primary text-xl">Ceremind</span>
                    </div>
                    <nav className="hidden md:flex space-x-6 lg:space-x-8">
                        <a onClick={(e) => { if (!showSection("story")) { e.preventDefault(); toast.info("Bientôt disponible ⏳"); } }} className="font-label-caps text-label-caps text-on-surface-variant hover:text-secondary transition-colors duration-300" href="#notre-histoire">Notre Histoire</a>
                        <a onClick={(e) => { if (!showSection("programme")) { e.preventDefault(); toast.info("Bientôt disponible ⏳"); } }} className="font-label-caps text-label-caps text-on-surface-variant hover:text-secondary transition-colors duration-300" href="#le-grand-jour">Le Grand Jour</a>
                        <a onClick={(e) => { if (!showSection("infos")) { e.preventDefault(); toast.info("Bientôt disponible ⏳"); } }} className="font-label-caps text-label-caps text-on-surface-variant hover:text-secondary transition-colors duration-300" href="#le-lieu">Le Lieu</a>
                        <a onClick={(e) => { if (!showSection("lodgings")) { e.preventDefault(); toast.info("Bientôt disponible ⏳"); } }} className="font-label-caps text-label-caps text-on-surface-variant hover:text-secondary transition-colors duration-300" href="#hebergements">Hébergements</a>
                        <a onClick={(e) => { if (!showSection("gifts")) { e.preventDefault(); toast.info("Bientôt disponible ⏳"); } }} className="font-label-caps text-label-caps text-on-surface-variant hover:text-secondary transition-colors duration-300" href="#liste-de-mariage">Liste de Mariage</a>
                    </nav>
                </div>
            </header>

            <main>
                {/* Hero Section */}
                <section className="relative w-full h-[819px] md:h-screen flex items-center justify-center">
                    <div className="absolute inset-0 z-0">
                        <img alt="Hero" className="w-full h-full object-cover object-center filter brightness-75" src={heroImage || "/images/theme-assets/asset_1ebb0264.png"} />
                    </div>
                    <div className="relative z-10 text-center px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto text-surface-container-lowest">
                        <h1 className="font-display-lg text-[48px] md:text-[96px] md:leading-[104px] tracking-tight mb-6 drop-shadow-lg text-white font-bold">{wedding?.partner1} &amp; {wedding?.partner2}</h1>
                        <div className="weave-divider w-24 mx-auto mb-6"></div>
                        <p className="font-label-caps text-label-caps text-primary-fixed tracking-widest uppercase text-white drop-shadow-md">{dateStr}</p>
                        <p className="font-body-lg text-body-lg mt-4 text-white opacity-90 drop-shadow-md">{wedding?.venue || "Le lieu magique"}</p>
                    </div>
                </section>

                {/* Our Story */}
                {showSection("story") && wedding?.story && (
                    <section className="py-24 px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="notre-histoire">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                            <div className="md:col-span-5 md:col-start-2 order-2 md:order-1 fade-in-up visible">
                                <h2 className="font-display-md text-[40px] font-bold text-primary mb-6">Notre Histoire</h2>
                                <p className="font-body-lg text-lg text-on-surface-variant leading-relaxed whitespace-pre-wrap">{wedding.story}</p>
                            </div>
                            <div className="md:col-span-5 md:col-start-8 order-1 md:order-2 mb-8 md:mb-0 relative fade-in-up visible">
                                <div className="absolute -inset-4 border border-outline-variant opacity-50 translate-x-4 translate-y-4 madras-pattern-bg"></div>
                                <img alt="Notre histoire" className="relative w-full h-auto object-cover transition-all duration-700 shadow-sm border border-black/10" src={accentImage || "/images/theme-assets/asset_26a6389e.png"} />
                            </div>
                        </div>
                    </section>
                )}

                {/* Programme */}
                {showSection("programme") && wedding?.programme && wedding.programme.length > 0 && (
                    <section className="py-24 bg-surface-container-low px-margin-mobile md:px-margin-desktop" id="le-grand-jour">
                        <div className="max-w-container-max mx-auto">
                            <div className="text-center mb-16 fade-in-up visible">
                                <h2 className="font-display-md text-[40px] font-bold text-primary mb-4">Le Grand Jour</h2>
                                <div className="weave-divider w-16 mx-auto"></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {wedding.programme.map((item, idx) => (
                                    <div key={idx} className="bg-surface-container-lowest p-8 border border-black/10 relative group fade-in-up visible" style={{transitionDelay: `${idx * 100}ms`}}>
                                        <div className="absolute top-0 left-0 w-full h-1 madras-pattern-bg scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
                                        <div className="mb-6 text-tertiary">
                                            <span className="material-symbols-outlined text-[40px]" style={{fontVariationSettings: "'FILL' 0"}}>
                                                {idx === 0 ? "favorite" : idx === 1 ? "local_bar" : "restaurant"}
                                            </span>
                                        </div>
                                        <h3 className="font-display-md text-[28px] font-bold mb-2 text-on-surface">{item.title}</h3>
                                        <p className="font-label-caps text-xs tracking-widest uppercase text-secondary mb-4">{item.time}</p>
                                        <p className="font-body-md text-on-surface-variant">{item.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Venue */}
                {showSection("infos") && wedding?.venue && (
                    <section className="py-24 px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="le-lieu">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div className="relative h-[500px] border border-black/10 p-2 bg-surface-container-low flex items-center justify-center madras-pattern-bg fade-in-up visible">
                                <div className="absolute inset-2 bg-surface-container-low flex items-center justify-center">
                                    <span className="material-symbols-outlined text-[64px] text-outline-variant">map</span>
                                </div>
                            </div>
                            <div className="md:pl-12 fade-in-up visible" style={{transitionDelay: "100ms"}}>
                                <h2 className="font-display-md text-[40px] font-bold text-primary mb-6">Le Lieu</h2>
                                <h3 className="font-display-md text-[28px] font-bold mb-4 text-on-surface">{wedding.venue}</h3>
                                {wedding.access_info && (
                                    <p className="font-body-lg text-lg text-on-surface-variant mb-8 whitespace-pre-wrap">{wedding.access_info}</p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* Dress Code Section */}
                {showSection("dress_code") && (wedding?.dress_code || (wedding?.dress_code_colors && wedding.dress_code_colors.length > 0)) && (
                    <section className="py-24 px-margin-mobile md:px-margin-desktop bg-surface" id="dress-code">
                        <div className="max-w-container-max mx-auto">
                            <div className="text-center mb-16 fade-in-up visible">
                                <span className="font-label-caps text-xs tracking-widest text-primary mb-4 block uppercase">Esthétique</span>
                                <h2 className="font-display-md text-[40px] font-bold text-primary">Dress Code</h2>
                                {wedding.dress_code && (
                                    <p className="font-body-lg text-lg text-on-surface-variant mt-4 max-w-2xl mx-auto">{wedding.dress_code}</p>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                                {/* Palette */}
                                {wedding.dress_code_colors && wedding.dress_code_colors.length > 0 && (
                                    <div className={`${!wedding.hero_image && !wedding.accent_image ? 'md:col-span-12 max-w-3xl mx-auto w-full' : 'md:col-span-4'} flex flex-col gap-6 fade-in-up visible`}>
                                        <div className="bg-surface-container-low p-8 rounded-sm editorial-shadow border border-outline-variant/30">
                                            <h3 className="font-label-caps text-xs text-primary mb-8 uppercase tracking-widest text-center font-semibold">Charte Chromatique</h3>
                                            <div className="flex flex-col gap-6">
                                                {wedding.dress_code_colors.map((color, idx) => (
                                                    <div key={idx} className="flex items-center gap-4 group cursor-pointer p-2 rounded hover:bg-white transition-all duration-300 relative overflow-visible swatch-item" onClick={() => handleCopy(color)}>
                                                        <div className="w-14 h-14 rounded-full swatch-glow transition-transform duration-500 group-hover:scale-110 z-10 shadow-sm" style={{backgroundColor: color}}></div>
                                                        <div className="flex flex-col">
                                                            <span className="font-display-md text-lg text-on-surface font-bold">Couleur {idx + 1}</span>
                                                            <div className="relative overflow-hidden h-4">
                                                                <span className={`font-label-caps text-[10px] text-on-surface-variant/60 transition-transform duration-300 block ${copiedColor === color ? '-translate-y-full' : 'group-hover:-translate-y-full'}`}>{color.toUpperCase()}</span>
                                                                <span className={`font-label-caps text-[10px] text-primary font-bold absolute top-0 left-0 transition-transform duration-300 uppercase tracking-tighter ${copiedColor === color ? 'translate-y-0 text-tertiary' : 'translate-y-full group-hover:translate-y-0'}`}>{copiedColor === color ? 'COPIÉ !' : 'COPIER'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Moodboard */}
                                <div className={`${(!wedding.dress_code_colors || wedding.dress_code_colors.length === 0) ? 'md:col-span-12 max-w-4xl mx-auto w-full' : 'md:col-span-8'} fade-in-up visible`} style={{transitionDelay: '200ms'}}>
                                    <div className="bg-white rounded-sm overflow-hidden editorial-shadow border border-outline-variant/30 group">
                                        <div className="p-6 flex justify-between items-center border-b border-surface-container">
                                            <h3 className="font-label-caps text-xs text-on-surface-variant uppercase tracking-widest font-semibold">Inspirations Visuelles</h3>
                                            <div className="flex gap-2">
                                                <span className="px-3 py-1 bg-surface-container-low text-on-surface rounded-full text-[10px] font-label-caps font-semibold">SILK</span>
                                                <span className="px-3 py-1 bg-surface-container-low text-on-surface rounded-full text-[10px] font-label-caps font-semibold">LINEN</span>
                                            </div>
                                        </div>
                                        <div className="p-8">
                                            <div 
                                                className="relative overflow-hidden rounded-sm parallax-container cursor-crosshair"
                                                onMouseMove={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const x = e.clientX - rect.left;
                                                    const y = e.clientY - rect.top;
                                                    const centerX = rect.width / 2;
                                                    const centerY = rect.height / 2;
                                                    const moveX = (x - centerX) / 30;
                                                    const moveY = (y - centerY) / 30;
                                                    const img = e.currentTarget.querySelector('img');
                                                    if (img) img.style.transform = `scale(1.05) translate(${moveX}px, ${moveY}px)`;
                                                }}
                                                onMouseLeave={(e) => {
                                                    const img = e.currentTarget.querySelector('img');
                                                    if (img) img.style.transform = 'scale(1) translate(0, 0)';
                                                }}
                                            >
                                                <img alt="Wedding Guest Dress Code Moodboard" className="w-full h-auto object-cover rounded-sm parallax-image transition-transform duration-500" src={wedding.dress_code_image || DRESS_CODE_MOODBOARD_FALLBACK} />
                                                <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-sm"></div>
                                            </div>
                                            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div>
                                                    <h4 className="font-display-md text-[28px] font-bold mb-3 text-on-surface">Pour Elles</h4>
                                                    <p className="font-body-md text-on-surface-variant">Robes fluides et colorées, matières nobles comme la soie ou le lin. N'hésitez pas à oser les motifs.</p>
                                                </div>
                                                <div>
                                                    <h4 className="font-display-md text-[28px] font-bold mb-3 text-on-surface">Pour Eux</h4>
                                                    <p className="font-body-md text-on-surface-variant">Costumes légers ou tenues de cocktail élégantes. Un détail de couleur sera du plus bel effet.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Accommodations */}
                {showSection("lodgings") && (
                    <section className="py-24 bg-surface-container-low px-margin-mobile md:px-margin-desktop" id="hebergements">
                        <div className="max-w-container-max mx-auto">
                            <div className="text-center mb-16 fade-in-up visible">
                                <h2 className="font-display-md text-[40px] font-bold text-primary mb-4">Où Séjourner</h2>
                                <div className="weave-divider w-16 mx-auto mb-8"></div>
                                <p className="font-body-lg text-lg text-on-surface-variant max-w-2xl mx-auto whitespace-pre-wrap">{wedding?.accommodations || "Nous avons sélectionné quelques options à proximité pour votre confort."}</p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {(wedding?.lodgings?.length > 0 ? wedding.lodgings : [
                                    { name: "L'Aigle Noir", capacity: "5 min", description: "Un hôtel de charme au coeur de la ville.", image: "/images/theme-assets/asset_86c86681.png", booking_url: "#" }
                                ]).map((lodging, idx) => (
                                    <div key={idx} className="bg-surface p-6 relative group overflow-hidden fade-in-up visible" style={{transitionDelay: `${idx * 0.1}s`}}>
                                        <div className="absolute inset-x-0 top-0 h-1 madras-pattern-bg"></div>
                                        {lodging.image && (
                                            <div className="h-40 -mx-6 -mt-6 mb-6 overflow-hidden">
                                                <img src={lodging.image} alt={lodging.name} className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <h3 className="font-display-md text-[24px] font-bold text-on-surface mb-2">{lodging.name}</h3>
                                        <span className="font-label-caps text-xs text-primary mb-4 block tracking-wider uppercase">{lodging.capacity || "HÉBERGEMENT"}</span>
                                        <p className="font-body-md text-on-surface-variant mb-6">{lodging.description}</p>
                                        {lodging.booking_url && <a href={lodging.booking_url} target="_blank" rel="noreferrer" className="text-secondary font-label-caps text-xs uppercase tracking-wider hover:text-primary transition-colors">Réserver</a>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Gifts / Liste de Mariage */}
                {showSection("gifts") && wedding?.show_gifts !== false && (
                    <section className="py-24 px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto" id="liste-de-mariage">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div className="order-2 md:order-1 relative fade-in-up visible">
                                <img alt="Cadeaux" className="w-full h-[600px] object-cover shadow-md" src={accentImage || "/images/theme-assets/asset_5179a37b.png"} />
                            </div>
                            <div className="order-1 md:order-2 md:pl-12 mb-12 md:mb-0 fade-in-up visible" style={{transitionDelay: '100ms'}}>
                                <h2 className="font-display-md text-[40px] font-bold text-primary mb-6">Liste de Mariage</h2>
                                <p className="font-body-lg text-lg text-on-surface-variant mb-8">
                                    Votre présence à nos côtés est le plus beau des cadeaux. Si vous souhaitez néanmoins participer à notre bonheur, nous avons ouvert une liste pour notre lune de miel et nos futurs projets.
                                </p>
                                
                                {gifts.length > 0 && (
                                    <div className="space-y-6">
                                        {gifts.map(gift => (
                                            <div key={gift.id} className="bg-surface-container-low p-6 relative">
                                                <div className="absolute inset-y-0 left-0 w-1 madras-pattern-bg"></div>
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-display-md text-[22px] font-bold mb-2 text-on-surface">{gift.name}</h4>
                                                        {gift.description && <p className="font-body-md text-on-surface-variant">{gift.description}</p>}
                                                    </div>
                                                    {!gift.claimed_by && (
                                                        <button 
                                                            onClick={() => setClaimingGift(gift)}
                                                            className="text-primary font-label-caps text-xs uppercase tracking-wider hover:text-secondary transition-colors"
                                                        >
                                                            Offrir
                                                        </button>
                                                    )}
                                                    {gift.claimed_by && (
                                                        <span className="text-on-surface-variant text-xs italic">Déjà offert</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Gift Claim Modal */}
                        {claimingGift && (
                            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                                <div className="bg-surface-container-lowest p-8 max-w-md w-full relative">
                                    <button onClick={() => setClaimingGift(null)} className="absolute top-4 right-4">
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                    <h3 className="font-display-md text-2xl font-bold mb-4">{claimingGift.type === "cash" ? `Contribuer à ${claimingGift.name}` : `Offrir ${claimingGift.name}`}</h3>
                                    <form onSubmit={handleClaimGift} className="space-y-4">
                                        <div>
                                            <label className="block text-sm mb-1">Votre Nom</label>
                                            <input 
                                                type="text" 
                                                required 
                                                className="w-full border p-2 bg-transparent"
                                                value={guestName} 
                                                onChange={e => setGuestName(e.target.value)}
                                            />
                                        </div>
                                        {claimingGift.type === "cash" && (
                                            <div>
                                                <label className="block text-sm mb-1">Votre participation (€)</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="0.01"
                                                    required
                                                    className="w-full border p-2 bg-transparent"
                                                    value={contributionAmount}
                                                    onChange={e => setContributionAmount(e.target.value)}
                                                />
                                            </div>
                                        )}
                                        <button disabled={isClaiming} type="submit" className="w-full bg-primary text-white py-2 font-bold hover:bg-primary-container hover:text-on-primary-container transition-colors">
                                            {isClaiming ? "En cours..." : claimingGift.type === "cash" ? "Continuer" : "Confirmer"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* Guestbook */}
                {showSection("guestbook") && (
                    <section className="py-24 bg-surface-variant px-margin-mobile md:px-margin-desktop text-center">
                        <div className="max-w-3xl mx-auto">
                            <span className="material-symbols-outlined text-[48px] text-primary mb-6">format_quote</span>
                            <h2 className="font-display-md text-[40px] font-bold text-on-surface mb-8">Mots Doux</h2>
                            
                            <div className="space-y-8 mb-12 text-left">
                                {messages.map((m) => (
                                    <div key={m.id} className="bg-surface-container-lowest p-6 border border-black/10">
                                        <p className="font-body-lg text-lg text-on-surface-variant italic mb-4">"{m.content}"</p>
                                        <p className="font-label-caps text-xs text-secondary uppercase tracking-widest">- {m.guest_name}</p>
                                    </div>
                                ))}
                            </div>

                            <form onSubmit={handleMessageSubmit} className="mt-12 bg-surface-container-lowest p-6 border border-black/10 text-left">
                                <h3 className="font-display-md text-xl font-bold mb-4">Laissez un mot</h3>
                                <div className="space-y-4">
                                    <input 
                                        type="text" 
                                        placeholder="Votre nom" 
                                        required 
                                        className="w-full border-b border-outline-variant bg-transparent py-2 outline-none focus:border-primary transition-colors"
                                        value={newMessage.guest_name}
                                        onChange={e => setNewMessage({...newMessage, guest_name: e.target.value})}
                                    />
                                    <textarea 
                                        placeholder="Votre message" 
                                        required
                                        rows={3} 
                                        className="w-full border-b border-outline-variant bg-transparent py-2 outline-none focus:border-primary transition-colors resize-none"
                                        value={newMessage.content}
                                        onChange={e => setNewMessage({...newMessage, content: e.target.value})}
                                    />
                                    <button 
                                        disabled={isSubmitting}
                                        className="bg-primary-container text-on-primary-container px-8 py-3 font-label-caps text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-primary hover:text-white transition-colors"
                                    >
                                        {isSubmitting ? "Envoi..." : "Ajouter un mot doux"}
                                    </button>
                                </div>
                            </form>
                            
                            <div className="flex justify-center space-x-2 mt-8">
                                <div className="w-2 h-2 rounded-full madras-pattern-bg"></div>
                                <div className="w-2 h-2 rounded-full bg-outline-variant"></div>
                                <div className="w-2 h-2 rounded-full bg-outline-variant"></div>
                            </div>
                        </div>
                    </section>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-surface-container-highest w-full py-16 relative mt-auto">
                <div className="absolute top-0 left-0 w-full h-2 madras-pattern-bg"></div>
                <div className="flex flex-col md:flex-row justify-between items-center px-margin-mobile md:px-margin-desktop space-y-8 md:space-y-0 max-w-container-max mx-auto">
                    <div className="h-12">
                        <span className="font-display-md text-primary text-xl">Ceremind</span>
                    </div>
                    <div className="font-body-md text-sm text-on-surface-variant">
                        © {new Date().getFullYear()} Ceremind. Tous droits réservés.
                    </div>
                </div>
            </footer>
        </div>
    );
}
